import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import AdminLayout from '../components/AdminLayout';
import { getAccessToken } from '../utils/authSession';
import { getUploadUrl, toUploadPath } from '../utils/uploadUrl';

function AdminSeniorManagement() {
  const [management, setManagement] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    designation: '',
    image: '',
    bio: '',
    facebook: '',
    twitter: '',
    linkedin: '',
    instagram: '',
    status: 'active'
  });
  const [formError, setFormError] = useState('');
  const [formLoading, setFormLoading] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const token = getAccessToken();
    if (!token) {
      navigate('/admin/login');
      return;
    }
    fetchManagement();
  }, [navigate, search]);

  const fetchManagement = async () => {
    try {
      setLoading(true);
      const token = getAccessToken();
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      params.append('include_inactive', 'true');

      const response = await axios.get(`/api/senior-management?${params}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      setManagement(response.data.management || []);
    } catch (error) {
      if (error.response?.status === 401) {
        navigate('/admin/login');
      }
      console.error('Error fetching management:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      alert('Image size must be less than 5MB');
      return;
    }

    setUploadingImage(true);
    try {
      const token = getAccessToken();
      const uploadFormData = new FormData();
      uploadFormData.append('image', file);

      const response = await axios.post('/api/upload', uploadFormData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      const imageUrl = toUploadPath(response.data.filename || response.data.path || response.data.imageUrl);
      setFormData(prev => ({ ...prev, image: imageUrl }));
    } catch (error) {
      alert('Failed to upload image');
      console.error('Error uploading image:', error);
    } finally {
      setUploadingImage(false);
    }
  };

  const handleAdd = () => {
    setFormData({
      name: '',
      designation: '',
      image: '',
      bio: '',
      facebook: '',
      twitter: '',
      linkedin: '',
      instagram: '',
      status: 'active'
    });
    setFormError('');
    setShowAddModal(true);
  };

  const handleEdit = async (member) => {
    try {
      const token = getAccessToken();
      const response = await axios.get(`/api/senior-management/${member.id}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      const fullMember = response.data;
      
      setEditingMember(fullMember);
      setFormData({
        name: fullMember.name || '',
        designation: fullMember.designation || '',
        image: fullMember.image || '',
        bio: fullMember.bio || '',
        facebook: fullMember.facebook || '',
        twitter: fullMember.twitter || '',
        linkedin: fullMember.linkedin || '',
        instagram: fullMember.instagram || '',
        status: fullMember.status || 'active'
      });
      setFormError('');
      setShowEditModal(true);
    } catch (error) {
      console.error('Error fetching member details:', error);
      setEditingMember(member);
      setFormData({
        name: member.name || '',
        designation: member.designation || '',
        image: member.image || '',
        bio: member.bio || '',
        facebook: member.facebook || '',
        twitter: member.twitter || '',
        linkedin: member.linkedin || '',
        instagram: member.instagram || '',
        status: member.status || 'active'
      });
      setFormError('');
      setShowEditModal(true);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    setFormLoading(true);

    try {
      const trimmedName = (formData.name || '').trim();
      const trimmedDesignation = (formData.designation || '').trim();

      if (!trimmedName || trimmedName.length === 0) {
        setFormError('Name is required');
        setFormLoading(false);
        return;
      }

      if (!trimmedDesignation || trimmedDesignation.length === 0) {
        setFormError('Designation is required');
        setFormLoading(false);
        return;
      }

      const token = getAccessToken();
      const data = {
        ...formData,
        name: trimmedName,
        designation: trimmedDesignation,
        bio: (formData.bio || '').trim() || null,
        facebook: (formData.facebook || '').trim() || null,
        twitter: (formData.twitter || '').trim() || null,
        linkedin: (formData.linkedin || '').trim() || null,
        instagram: (formData.instagram || '').trim() || null
      };

      if (editingMember) {
        await axios.put(`/api/senior-management/${editingMember.id}`, data, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        alert('Management member updated successfully!');
      } else {
        await axios.post('/api/senior-management', data, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        alert('Management member created successfully!');
      }

      setShowAddModal(false);
      setShowEditModal(false);
      setEditingMember(null);
      await fetchManagement();
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Failed to save management member';
      setFormError(errorMessage);
      
      if (error.response?.status === 401) {
        alert('Your session has expired. Please log in again.');
        navigate('/admin/login');
      }
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this management member?')) return;

    try {
      const token = getAccessToken();
      await axios.delete(`/api/senior-management/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      alert('Management member deleted successfully!');
      fetchManagement();
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to delete management member');
    }
  };

  const filteredManagement = management.filter(member =>
    member.name?.toLowerCase().includes(search.toLowerCase()) ||
    member.designation?.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return (
      <AdminLayout>
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="container-fluid py-4">
        <div className="row mb-4">
          <div className="col">
            <h1 className="h3 mb-0">Senior Management</h1>
          </div>
          <div className="col-auto">
            <button className="btn btn-primary" onClick={handleAdd}>
              <i className="bi bi-plus-circle me-2"></i>
              Add Member
            </button>
          </div>
        </div>

        {/* Search */}
        <div className="row mb-4">
          <div className="col-md-4">
            <input
              type="text"
              className="form-control"
              placeholder="Search by name or designation..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        {/* Management Table */}
        <div className="card">
          <div className="card-body">
            <div className="table-responsive">
              <table className="table table-hover">
                <thead>
                  <tr>
                    <th>Image</th>
                    <th>Name</th>
                    <th>Designation</th>
                    <th>Social Media</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredManagement.length === 0 ? (
                    <tr>
                      <td colSpan="8" className="text-center text-muted py-4">
                        No management members found
                      </td>
                    </tr>
                  ) : (
                    filteredManagement.map((member) => (
                      <tr key={member.id}>
                        <td>
                          {member.image ? (
                            <img
                              src={member.image}
                              alt={member.name}
                              style={{ width: '50px', height: '50px', objectFit: 'cover', borderRadius: '50%' }}
                              onError={(e) => {
                                e.target.src = '/img/Male Professional Headshot - East African.jpeg';
                              }}
                            />
                          ) : (
                            <div style={{ width: '50px', height: '50px', borderRadius: '50%', backgroundColor: '#ddd' }}></div>
                          )}
                        </td>
                        <td>{member.name}</td>
                        <td>{member.designation}</td>
                        <td>
                          <div className="d-flex gap-2">
                            {member.facebook && <i className="bi bi-facebook text-primary" title="Facebook"></i>}
                            {member.twitter && <i className="bi bi-twitter text-info" title="Twitter"></i>}
                            {member.linkedin && <i className="bi bi-linkedin text-primary" title="LinkedIn"></i>}
                            {member.instagram && <i className="bi bi-instagram text-danger" title="Instagram"></i>}
                            {!member.facebook && !member.twitter && !member.linkedin && !member.instagram && '-'}
                          </div>
                        </td>
                        <td>
                          <span className={`badge ${member.status === 'active' ? 'bg-success' : 'bg-secondary'}`}>
                            {member.status}
                          </span>
                        </td>
                        <td>
                          <button
                            className="btn btn-sm btn-primary me-2"
                            onClick={() => handleEdit(member)}
                          >
                            <i className="bi bi-pencil me-1"></i>
                            Edit
                          </button>
                          <button
                            className="btn btn-sm btn-danger"
                            onClick={() => handleDelete(member.id)}
                          >
                            <i className="bi bi-trash me-1"></i>
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Add Modal */}
        <div className={`modal fade ${showAddModal ? 'show' : ''}`} style={{ display: showAddModal ? 'block' : 'none' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Add Management Member</h5>
                <button type="button" className="btn-close" onClick={() => setShowAddModal(false)}></button>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="modal-body">
                  {formError && (
                    <div className="alert alert-danger">{formError}</div>
                  )}
                  <div className="mb-3">
                    <label className="form-label">Name *</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Designation *</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.designation}
                      onChange={(e) => setFormData({ ...formData, designation: e.target.value })}
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Image</label>
                    <input
                      type="file"
                      className="form-control"
                      accept="image/*"
                      onChange={handleImageUpload}
                      disabled={uploadingImage}
                    />
                    {formData.image && (
                      <div className="mt-2">
                        <img
                          src={getUploadUrl(formData.image)}
                          alt="Preview"
                          style={{ maxWidth: '200px', maxHeight: '200px', objectFit: 'contain' }}
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                      </div>
                    )}
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Bio</label>
                    <textarea
                      className="form-control"
                      rows="3"
                      value={formData.bio}
                      onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                    />
                  </div>
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label className="form-label">
                        <i className="bi bi-facebook me-2" style={{ color: '#1877f2' }}></i>Facebook URL
                      </label>
                      <input
                        type="url"
                        className="form-control"
                        placeholder="https://facebook.com/username"
                        value={formData.facebook}
                        onChange={(e) => setFormData({ ...formData, facebook: e.target.value })}
                      />
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label">
                        <i className="bi bi-twitter me-2" style={{ color: '#1da1f2' }}></i>Twitter URL
                      </label>
                      <input
                        type="url"
                        className="form-control"
                        placeholder="https://twitter.com/username"
                        value={formData.twitter}
                        onChange={(e) => setFormData({ ...formData, twitter: e.target.value })}
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label className="form-label">
                        <i className="bi bi-linkedin me-2" style={{ color: '#0077b5' }}></i>LinkedIn URL
                      </label>
                      <input
                        type="url"
                        className="form-control"
                        placeholder="https://linkedin.com/in/username"
                        value={formData.linkedin}
                        onChange={(e) => setFormData({ ...formData, linkedin: e.target.value })}
                      />
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label">
                        <i className="bi bi-instagram me-2" style={{ color: '#e4405f' }}></i>Instagram URL
                      </label>
                      <input
                        type="url"
                        className="form-control"
                        placeholder="https://instagram.com/username"
                        value={formData.instagram}
                        onChange={(e) => setFormData({ ...formData, instagram: e.target.value })}
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Status</label>
                      <select
                        className="form-select"
                        value={formData.status}
                        onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                      >
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                      </select>
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowAddModal(false)}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={formLoading}>
                    {formLoading ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
        {showAddModal && <div className="modal-backdrop fade show"></div>}

        {/* Edit Modal */}
        <div className={`modal fade ${showEditModal ? 'show' : ''}`} style={{ display: showEditModal ? 'block' : 'none' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Edit Management Member</h5>
                <button type="button" className="btn-close" onClick={() => setShowEditModal(false)}></button>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="modal-body">
                  {formError && (
                    <div className="alert alert-danger">{formError}</div>
                  )}
                  <div className="mb-3">
                    <label className="form-label">Name *</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Designation *</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.designation}
                      onChange={(e) => setFormData({ ...formData, designation: e.target.value })}
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Image</label>
                    <input
                      type="file"
                      className="form-control"
                      accept="image/*"
                      onChange={handleImageUpload}
                      disabled={uploadingImage}
                    />
                    {formData.image && (
                      <div className="mt-2">
                        <img
                          src={getUploadUrl(formData.image)}
                          alt="Preview"
                          style={{ maxWidth: '200px', maxHeight: '200px', objectFit: 'contain' }}
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                      </div>
                    )}
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Bio</label>
                    <textarea
                      className="form-control"
                      rows="3"
                      value={formData.bio}
                      onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                    />
                  </div>
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label className="form-label">
                        <i className="bi bi-facebook me-2" style={{ color: '#1877f2' }}></i>Facebook URL
                      </label>
                      <input
                        type="url"
                        className="form-control"
                        placeholder="https://facebook.com/username"
                        value={formData.facebook}
                        onChange={(e) => setFormData({ ...formData, facebook: e.target.value })}
                      />
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label">
                        <i className="bi bi-twitter me-2" style={{ color: '#1da1f2' }}></i>Twitter URL
                      </label>
                      <input
                        type="url"
                        className="form-control"
                        placeholder="https://twitter.com/username"
                        value={formData.twitter}
                        onChange={(e) => setFormData({ ...formData, twitter: e.target.value })}
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label className="form-label">
                        <i className="bi bi-linkedin me-2" style={{ color: '#0077b5' }}></i>LinkedIn URL
                      </label>
                      <input
                        type="url"
                        className="form-control"
                        placeholder="https://linkedin.com/in/username"
                        value={formData.linkedin}
                        onChange={(e) => setFormData({ ...formData, linkedin: e.target.value })}
                      />
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label">
                        <i className="bi bi-instagram me-2" style={{ color: '#e4405f' }}></i>Instagram URL
                      </label>
                      <input
                        type="url"
                        className="form-control"
                        placeholder="https://instagram.com/username"
                        value={formData.instagram}
                        onChange={(e) => setFormData({ ...formData, instagram: e.target.value })}
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Status</label>
                      <select
                        className="form-select"
                        value={formData.status}
                        onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                      >
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                      </select>
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowEditModal(false)}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={formLoading}>
                    {formLoading ? 'Saving...' : 'Update'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
        {showEditModal && <div className="modal-backdrop fade show"></div>}
      </div>
    </AdminLayout>
  );
}

export default AdminSeniorManagement;
